import { Link, useNavigate } from '@remix-run/react'
import algoliasearch from 'algoliasearch'
import { useAtomValue, useSetAtom } from 'jotai'
import { LucideExternalLink } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useMap } from 'react-map-gl'
import { aOrganization, aSelectedListing, aUser } from '~/atoms'
import { Badge, Button, Command } from '~/components/ui'
import { useDebounce, useEmbed, useMapCardSizes, useMapEaseTo } from '~/hooks'
import {
  STATUSES,
  TYPES,
  cn,
  fmtAddress,
  getPropertyTypeClassName,
} from '~/utils'

const algoliaClient = algoliasearch(
  'P9SMCQDPUI',
  '42a1fe78d9e75aeed6286378781b74ab'
)
const listingsIndex = algoliaClient.initIndex('listings_1')

export default function SearchListings({
  search,
  setIsOpen,
}: {
  search: string
  setIsOpen: (isOpen: boolean) => void
}) {
  const organization = useAtomValue(aOrganization)
  const user = useAtomValue(aUser)
  const mapRef = useMap()
  const mapEaseTo = useMapEaseTo(mapRef as any)
  const navigate = useNavigate()
  const setSelectedListing = useSetAtom(aSelectedListing)
  const [results, setResults] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const mapCardSizes = useMapCardSizes()
  const [inputCleared, setInputCleared] = useState(false)
  const debouncedSearch = useDebounce(search, 200)
  const [status, setStatus] = useState<string>(
    organization!.options.appraisersOnly ? 'sold' : 'active'
  )
  const { isEmbed } = useEmbed()

  const searchListings = useCallback(async () => {
    if (debouncedSearch.trim().length < 3) return

    // const getBoundingBox = () => {
    //   const bounds = mapRef.current!.getBounds()
    //   const ne = bounds.getNorthEast()
    //   const sw = bounds.getSouthWest()
    //   // return [[ne.lat, ne.lng, sw.lat, sw.lng]]
    //   return [[sw.lat, sw.lng, ne.lat, ne.lng]]
    // }
    setIsLoading(true)
    let filters =
      !isEmbed &&
      organization!.options.searchOrganizationSlugs &&
      organization!.options.searchOrganizationSlugs.length > 0
        ? `(${[
            organization!.slug,
            ...organization!.options.searchOrganizationSlugs,
          ]
            .map((slug: string) => `organization.slug:${slug}`)
            .join(' OR ')})`
        : `organization.slug:${organization!.slug}`
    filters = `(${filters} OR externalMembershipSlugs:${organization!.slug})`
    if (status && status !== 'all') {
      filters += ` AND status:${status}`
    } else if (organization!.options.appraisersOnly) {
      // sold
      // leased
      filters += ` AND (status:sold OR status:leased)`
    } else {
      filters += ` AND (status:active OR status:pending OR status:sold OR status:leased OR status:withdrawn OR status:expired)`
    }
    if (!user) {
      filters += ` AND NOT isPublic:false`
    }
    const searchParams: Record<string, any> = {
      hitsPerPage: 30,
      getRankingInfo: true,
      distinct: true,
      highlightPreTag: '<strong>',
      highlightPostTag: '</strong>',
      filters,
      // insideBoundingBox: !mapRef.current ? undefined : getBoundingBox(),
    }

    // Exclude tid, id, slug from search unless exact 8 character match for id/tid
    if (debouncedSearch.trim().length !== 8) {
      searchParams.restrictSearchableAttributes = [
        'name',
        'property.address',
        'property.city',
        'property.postalCode',
        'property.stateAbbr',
        'propertyTypes',
        'meta.description',
        'meta.highlights',
        'status',
        'type',
      ]
    }

    const { hits } = await listingsIndex.search(
      debouncedSearch.trim(),
      searchParams
    )
    setResults(hits)
    setIsLoading(false)
  }, [debouncedSearch, status, organization, user, mapRef])

  useEffect(() => {
    setTimeout(() => setInputCleared(true), 200)
  }, [])

  useEffect(() => {
    if (!inputCleared) return
    if (!debouncedSearch) {
      setResults([])
    } else {
      searchListings()
    }
  }, [debouncedSearch, searchListings])

  const renderStatusBadges = () => {
    const statusOptions = organization!.options.appraisersOnly
      ? {
          sold: 'Sold',
          leased: 'Fully Leased',
        }
      : STATUSES
    const publicStatuses = user
      ? [
          ...Object.keys(statusOptions).filter(
            (s) => s !== 'deleted' && s !== 'draft' && s !== 'expired'
          ),
          'all',
        ]
      : ['active', 'pending']
    return (
      <div className="flex gap-1">
        {publicStatuses.map((s) => {
          return (
            <Badge
              key={s}
              variant={s === status ? 'default' : 'secondary'}
              className="cursor-pointer"
              onClick={(e) => {
                if (s === status) {
                  return
                }
                if (e.metaKey) {
                  setStatus(s)
                } else {
                  setStatus(s)
                }
              }}>
              {s === 'all' ? 'Show All ' : statusOptions[s]}
            </Badge>
          )
        })}
      </div>
    )
  }

  // const renderPropertyTypeBadges = () => {
  //   return (
  //     <div className="mt-2 flex flex-wrap gap-1">
  //       {PROPERTY_TYPES.map((propertyType) => {
  //         return (
  //           <Badge key={propertyType} variant="secondary" className="">
  //             {propertyType}
  //           </Badge>
  //         )
  //       })}
  //     </div>
  //   )
  // }

  return (
    <>
      <Command.Group>
        <div className="px-2 py-3">
          {renderStatusBadges()}
          {/* {renderPropertyTypeBadges()} */}
        </div>
      </Command.Group>
      <Command.Empty className="p-4 pt-1">
        {isLoading
          ? 'Loading results...'
          : debouncedSearch.length > 2 && results.length === 0
            ? 'No results found'
            : 'Start typing to search listings'}
      </Command.Empty>
      <Command.Group>
        {results.map((result) => {
          const matchesDescriptionHighlights =
            (result._highlightResult.meta?.description?.matchLevel &&
              result._highlightResult.meta?.description?.matchLevel !==
                'none') ||
            (result._highlightResult.meta?.highlight?.matchLevel &&
              result._highlightResult.meta?.highlight?.matchLevel !== 'none')
          return (
            <Command.Item
              key={result._id}
              value={result._id ?? result.tid}
              className="listing-matched flex flex-col items-start"
              onSelect={() => {
                if (!mapRef.current) {
                  navigate(`/listings/${result.slug}`)
                } else {
                  setIsOpen(false)
                  setTimeout(() => {
                    setSelectedListing(result)
                    const searchParams = new URLSearchParams(
                      window.location.search
                    )
                    searchParams.set('lat', result.property.latitude.toString())
                    searchParams.set(
                      'lng',
                      result.property.longitude.toString()
                    )
                    const z = mapRef.current!.getZoom()
                    searchParams.set('z', z < 18 ? '18' : z.toString())
                    navigate(`/${result.slug}?${searchParams}`)

                    if (mapRef) {
                      mapEaseTo({
                        center: [
                          result.property.longitude,
                          result.property.latitude,
                        ],
                        zoom: 18,
                        duration: 500,
                        padding: {
                          left: mapCardSizes.list + mapCardSizes.single,
                          bottom: 0,
                          right: 0,
                          top: 0,
                        },
                      })
                    }
                  }, 200)
                }
              }}>
              <div className="flex w-full items-start justify-between">
                <div>
                  <div className="mb-1 flex gap-2">
                    <p
                      className="text-base leading-tight"
                      dangerouslySetInnerHTML={{
                        __html:
                          result._highlightResult.name.matchLevel === 'none'
                            ? result.name
                            : result._highlightResult.name.value,
                      }}
                    />
                  </div>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: fmtAddress(
                        {
                          address:
                            result._highlightResult.property.address?.value ??
                            result.property.address,
                          address2:
                            result._highlightResult.property.address2?.value ??
                            result.property.address2,
                          city:
                            result._highlightResult.property.city?.value ??
                            result.property.city,
                          stateAbbr:
                            result._highlightResult.property.stateAbbr?.value ??
                            result.property.stateAbbr,
                          county:
                            result._highlightResult.property.county?.value ??
                            result.property.county,
                          postalCode:
                            result._highlightResult.property.postalCode
                              ?.value ?? result.property.postalCode,
                        },
                        { locationVisibility: result.locationVisibility }
                      ),
                    }}
                  />

                  {matchesDescriptionHighlights && (
                    <p className="italic">
                      Search found in description or highlights
                    </p>
                  )}
                  <div className="mt-1 flex gap-2">
                    <Badge
                      className={cn(
                        'px-1 text-xs leading-none',
                        result.type === 'sale' &&
                          'bg-[#0284c7] hover:bg-[#0284c7]',
                        result.type === 'lease' &&
                          'bg-[#c026d3] hover:bg-[#c026d3]',
                        result.type === 'saleLease' &&
                          'bg-[#4f46e5] hover:bg-[#4f46e5]'
                      )}>
                      {result.status === 'active'
                        ? TYPES[result.type]
                        : STATUSES[result.status]}
                    </Badge>
                    {result.isBusinessListing && (
                      <Badge className="bg-[#a21caf] text-xs hover:bg-[#a21caf]">
                        Business
                      </Badge>
                    )}
                    {result.propertyTypes.map(
                      (value: string, index: number) => {
                        return (
                          <Badge
                            key={index}
                            className={cn(
                              'text-xs',
                              getPropertyTypeClassName(value)
                            )}>
                            <span>{value}</span>
                          </Badge>
                        )
                      }
                    )}
                  </div>
                </div>
                {!!mapRef && (
                  <div className="">
                    <Button
                      asChild
                      variant="ghost"
                      size="map-sm"
                      className="absolute bottom-2 right-2 z-10 w-auto gap-2 bg-white px-1 ring-primary ring-offset-transparent hover:bg-white hover:outline-none hover:ring-1 hover:ring-offset-1">
                      <Link
                        to={`/listings/${result.slug}`}
                        target="_blank"
                        onClick={(e) => {
                          e.stopPropagation()
                        }}>
                        <LucideExternalLink className="!h-4 !w-4" />
                        New Tab
                      </Link>
                    </Button>
                  </div>
                )}
              </div>

              <span className="hidden">{result.objectID}</span>
              {/* {result._highlightResult.meta?.description?.matchLevel &&
              result._highlightResult.meta?.description?.matchLevel !==
                'none' && (
                <p
                  dangerouslySetInnerHTML={{
                    __html: result._highlightResult.meta?.description.value,
                  }}></p>
              )}
            {result._highlightResult.meta?.highlights?.matchLevel &&
              result._highlightResult.meta?.highlights?.matchLevel !==
                'none' && (
                <p
                  dangerouslySetInnerHTML={{
                    __html: result._highlightResult.meta?.highlights.value,
                  }}></p>
              )} */}
            </Command.Item>
          )
        })}
      </Command.Group>
    </>
  )
}
